/* ==========================================================================
   theme.js — behaviour shared by every page.

   Page-specific behaviour lives in its own file (home.js, collection.js,
   product.js, archive.js) and is loaded by the section that needs it. Anything
   reusable is exposed on window.Theme.
   ========================================================================== */

(function () {
  'use strict';

  const Theme = (window.Theme = window.Theme || {});

  /* --- Small helpers ----------------------------------------------------- */

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  Theme.$ = $;
  Theme.$$ = $$;

  // Reveal an element and start its transition in the same frame. A forced
  // reflow is used rather than requestAnimationFrame, which never fires while
  // the tab is backgrounded and would leave panels stuck half-open.
  Theme.transitionIn = function (el, cls, host) {
    el.hidden = false;
    void el.offsetHeight;
    (host || el).classList.add(cls);
  };

  /* --- BACK button ------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    const back = event.target.closest('[data-history-back]');
    if (!back) return;
    event.preventDefault();
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  });

  /* --- Currency selector ------------------------------------------------- */

  document.addEventListener('change', function (event) {
    const select = event.target.closest('[data-currency-select]');
    if (!select) return;
    const form = select.closest('form');
    if (form) form.submit();
  });

  /* --- Mobile menu ------------------------------------------------------- */

  function initMobileMenu() {
    const menu = $('[data-mobile-menu]');
    if (!menu) return;

    const toggles = $$('[data-menu-toggle]');
    const label = $('[data-menu-label]');

    function setOpen(open) {
      menu.hidden = false;
      void menu.offsetHeight; // start the transition from the closed position
      menu.classList.toggle('is-open', open);
      toggles.forEach((t) => t.setAttribute('aria-expanded', String(open)));
      if (label) {
        label.textContent = open
          ? label.dataset.closeLabel || 'CLOSE'
          : label.dataset.openLabel || 'MENU';
      }
      document.body.style.overflow = open ? 'hidden' : '';
      if (!open) {
        setTimeout(() => {
          if (!menu.classList.contains('is-open')) menu.hidden = true;
        }, 450);
      }
    }

    if (label) {
      label.dataset.openLabel = label.textContent.trim();
      label.dataset.closeLabel = 'CLOSE';
    }

    toggles.forEach((toggle) => {
      toggle.addEventListener('click', () =>
        setOpen(!menu.classList.contains('is-open'))
      );
    });

    $$('[data-menu-close]', menu).forEach((el) => {
      el.addEventListener('click', () => setOpen(false));
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && menu.classList.contains('is-open')) {
        setOpen(false);
      }
    });
  }

  /* --- Newsletter modal -------------------------------------------------- */

  function initNewsletterModal() {
    const modal = $('[data-newsletter-modal]');
    if (!modal) return;

    let lastFocused = null;

    function setOpen(open) {
      if (open) {
        lastFocused = document.activeElement;
        Theme.transitionIn(modal, 'is-open');
        const close = $('[data-modal-close]', modal);
        if (close) close.focus();
      } else {
        modal.classList.remove('is-open');
        setTimeout(() => {
          modal.hidden = true;
        }, 450);
        if (lastFocused) lastFocused.focus();
      }
      document.body.style.overflow = open ? 'hidden' : '';
    }

    document.addEventListener('click', function (event) {
      if (event.target.closest('[data-newsletter-open]')) {
        event.preventDefault();
        setOpen(true);
      } else if (
        modal.classList.contains('is-open') &&
        event.target.closest('[data-modal-close]')
      ) {
        setOpen(false);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && modal.classList.contains('is-open')) {
        setOpen(false);
      }
    });
  }

  /* --- Accordions -------------------------------------------------------- */
  /* One open at a time within a given [data-accordion] group. */

  function initAccordions(root = document) {
    $$('[data-accordion]', root).forEach((group) => {
      if (group.dataset.accReady) return;
      group.dataset.accReady = 'true';

      group.addEventListener('click', function (event) {
        const toggle = event.target.closest('[data-acc-toggle]');
        if (!toggle || !group.contains(toggle)) return;

        const item = toggle.closest('.acc__item');
        const willOpen = !item.classList.contains('is-open');

        $$('.acc__item', group).forEach((other) => {
          other.classList.remove('is-open');
          const btn = $('[data-acc-toggle]', other);
          if (btn) btn.setAttribute('aria-expanded', 'false');
        });

        if (willOpen) {
          item.classList.add('is-open');
          toggle.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  Theme.initAccordions = initAccordions;

  /* --- Snap-scroll image columns ----------------------------------------- */
  /*
     Snapping itself is pure CSS (scroll-snap-type). This only tracks which
     image is currently centred so the neighbours can be scaled down and faded,
     and so the home page can swap its blurred backdrop.
  */

  Theme.initSnapColumn = function (scroller, options = {}) {
    if (!scroller) return null;

    const items = $$('[data-snap-item]', scroller);
    if (!items.length) return null;

    let current = -1;

    function setActive(index) {
      if (index === current) return;
      current = index;
      items.forEach((item, i) => item.classList.toggle('is-active', i === index));
      if (typeof options.onChange === 'function') {
        options.onChange(items[index], index);
      }
    }

    const observer = new IntersectionObserver(
      function (entries) {
        let best = null;
        entries.forEach((entry) => {
          if (!best || entry.intersectionRatio > best.intersectionRatio) {
            best = entry;
          }
        });
        // Re-check every item so a fast scroll can't leave a stale winner.
        let bestIndex = current;
        let bestRatio = 0;
        const bounds = scroller.getBoundingClientRect();
        const mid = bounds.top + bounds.height / 2;
        items.forEach((item, i) => {
          const rect = item.getBoundingClientRect();
          const distance = Math.abs(rect.top + rect.height / 2 - mid);
          const score = 1 / (1 + distance);
          if (score > bestRatio) {
            bestRatio = score;
            bestIndex = i;
          }
        });
        setActive(bestIndex);
      },
      { root: scroller, threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    items.forEach((item) => observer.observe(item));
    setActive(options.startIndex || 0);

    return {
      items,
      goTo(index) {
        const item = items[index];
        if (item) scroller.scrollTo({ top: item.offsetTop - scroller.offsetTop });
      },
      get index() {
        return current;
      }
    };
  };

  /* --- Cart -------------------------------------------------------------- */

  Theme.cart = {
    async add(formData) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || 'Add to cart failed');
      }
      return response.json();
    },

    async get() {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' }
      });
      return response.json();
    },

    async change(payload) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      return response.json();
    },

    updateCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count > 0 ? ' (' + count + ')' : '';
      });
      const live = document.getElementById('cart-live-region');
      if (live) live.textContent = count + ' items in bag';
    }
  };

  /* --- Cart panel (right rail) --------------------------------------------
     Slides into whichever page's own right rail holds it — every page's own
     section now renders its cart panel the same way the product page always
     has (see snippets/cart-panel.liquid), so this is the one place that
     opens/closes/refreshes it, on every page alike. CART/BAG opens it in
     place instead of navigating away; the product page's add-to-cart opens
     it too (see product.js), so there's exactly one mechanism. Rails only
     exist on desktop — on mobile CART/BAG still navigates normally. */

  function initCartPanel() {
    const panel = $('[data-cart-panel]');
    if (!panel) return;

    const rail = panel.closest('.rail--right');
    if (!rail) return;

    const sectionId = rail.dataset.sectionId;
    const isMobile = () => window.matchMedia('(max-width: 899px)').matches;

    async function refresh() {
      if (!sectionId) return;
      const url =
        window.location.pathname + '?section_id=' + encodeURIComponent(sectionId);
      try {
        const response = await fetch(url);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-cart-panel] [data-cart-contents]');
        const current = panel.querySelector('[data-cart-contents]');
        if (fresh && current) current.replaceWith(fresh);
      } catch (e) {
        /* Leave the last good render in place. */
      }
    }

    function open() {
      rail.classList.add('is-cart-open');
    }

    function close() {
      rail.classList.remove('is-cart-open');
      // Harmless everywhere except the product page: clears the selected
      // size cell so it doesn't look chosen the next time the panel opens.
      document.querySelectorAll('[data-size].is-selected').forEach(function (cell) {
        cell.classList.remove('is-selected');
      });
    }

    Theme.cartPanel = {
      open: async function () {
        if (isMobile()) return;
        await refresh();
        open();
      },
      close,
      refresh
    };

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && rail.classList.contains('is-cart-open')) {
        close();
      }
    });

    document.addEventListener('click', function (event) {
      const link = event.target.closest('[data-cart-link]');
      if (link) {
        if (isMobile()) return;
        event.preventDefault();
        Theme.cartPanel.open();
        return;
      }

      if (
        rail.classList.contains('is-cart-open') &&
        !event.target.closest('[data-cart-panel]') &&
        !event.target.closest('[data-size]')
      ) {
        close();
      }
    });

    panel.addEventListener('click', async function (event) {
      const item = event.target.closest('[data-line]');
      if (!item) return;

      const line = parseInt(item.dataset.line, 10);
      const qtyEl = item.querySelector('[data-qty]');
      const quantity = qtyEl ? parseInt(qtyEl.textContent.trim(), 10) : 1;

      let next = null;
      if (event.target.closest('[data-qty-up]')) next = quantity + 1;
      if (event.target.closest('[data-qty-down]')) next = Math.max(0, quantity - 1);
      if (event.target.closest('[data-remove]')) next = 0;
      if (next === null) return;

      event.preventDefault();
      const cart = await Theme.cart.change({ line: line, quantity: next });
      Theme.cart.updateCount(cart.item_count);
      await refresh();
      if (cart.item_count === 0) close();
    });
  }

  /* --- Boot -------------------------------------------------------------- */

  function boot() {
    initMobileMenu();
    initNewsletterModal();
    initAccordions();
    initCartPanel();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', (event) => {
    initAccordions(event.target);
  });
})();
